"use client";

import React from "react";
import { ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import { Link } from "@/components/common/Link";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
const situations = [{
  title: "Foreclosure",
  description: "Facing foreclosure can be overwhelming. We provide fast cash offers to help you settle debts, avoid damaging your credit score, and move forward with peace of mind."
}, {
  title: "Inherited Property",
  description: "Managing an inherited home through probate is often stressful and time-consuming. We buy inherited properties entirely as-is, saving you from costly repairs, cleanouts, and prolonged holding costs."
}, {
  title: "Major Repairs",
  description: "Does your house need a new roof, foundation work, or a complete cosmetic update? Skip the contractors and the stress. We buy houses in any condition, requiring absolutely no repairs from you."
}, {
  title: "Financial Hardship",
  description: "If you need to liquidate assets quickly due to job loss, medical bills, or other unexpected financial strains, our streamlined cash process provides immediate relief without any hidden fees."
}, {
  title: "Problem Tenants",
  description: "Tired of dealing with difficult renters, late payments, or extensive property damage? We purchase rental properties with or without tenants currently in place, taking the headache completely off your hands."
}];
interface SituationStripProps {
  situation: {
    title: string;
    description: string;
  };
  isLast: boolean;
  "data-index": number;
}
function SituationStrip({
  situation,
  isLast,
  "data-index": dataIndex
}: SituationStripProps) {
  const {
    ref,
    isVisible
  } = useRevealOnScroll({
    threshold: 0.2
  });
  return <div ref={ref} data-index={dataIndex} className={`transition-all duration-700 ease-out ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
      <div className="group flex flex-col md:flex-row gap-6 md:gap-16 py-12 md:py-16 items-start md:items-center">
        <div className="md:w-1/3 shrink-0">
          <h3 className="text-3xl md:text-4xl font-bold uppercase tracking-tighter text-primary group-hover:text-primary/80 transition-colors">
            {situation.title}
          </h3>
        </div>
        <div className="md:w-2/3">
          <p className="text-lg md:text-xl text-muted-foreground leading-relaxed font-medium">
            {situation.description}
          </p>
        </div>
      </div>
      {!isLast && <Separator className="bg-border/50 h-[1px]" />}
    </div>;
}
export default function CommonSellerSituations() {
  const {
    ref: headerRef,
    isVisible: isHeaderVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  return <section id="common-seller-situations" className="dark bg-background text-foreground py-24 px-6 md:px-12 lg:px-24 overflow-hidden">
      <div className="max-w-7xl mx-auto">
        <div ref={headerRef} className={`mb-16 md:mb-24 transition-all duration-700 ease-out ${isHeaderVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
          <h2 className="text-4xl md:text-6xl font-bold tracking-tighter uppercase mb-6">
            Situations We <span className="text-primary">Resolve</span>
          </h2>
          <p className="text-xl text-muted-foreground max-w-2xl">
            No matter the circumstance, we offer a transparent, frictionless path to selling your property quickly and fairly.
          </p>
        </div>

        <div className="flex flex-col">
          <Separator className="bg-border/50 h-[1px]" />
          {situations.map((situation, i) => <SituationStrip key={situation.title} situation={situation} isLast={i === situations.length - 1} data-index={i} />)}
          <Separator className="bg-border/50 h-[1px]" />
        </div>

        <div className="mt-16 flex justify-center md:justify-start">
          <Button asChild size="lg" className="text-lg px-8 py-6 rounded-none font-bold uppercase tracking-wide">
            <Link to="/HomePage">
              Get a Cash Offer <ArrowRight className="ml-2 size-5" />
            </Link>
          </Button>
        </div>
      </div>
    </section>;
}